import React, { useMemo, useState } from 'react';
import './PlayerRoleSection.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import ActionButton from '../input/ActionButton';
import CustomDropdown from '../input/CustomDropdown';
import CustomTogglebox from '../input/CustomTogglebox';
import SegmentedControl from '../input/SegmentedControl';
import ParticipantGroupDot from '../participant-group/ParticipantGroupDot';
import ImpactPreview, { futureParticipationsLine } from './ImpactPreview';
import usePreviewRequest from './usePreviewRequest';
import apiErrorText from './apiErrorText';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import useRoleSettings from '../../hooks/useRoleSettings';
import {
  normalizeMembershipOrigin, normalizeParticipantGroup, normalizeRole, ROLE, ROLE_PERMISSIONS_TEXT,
  roleCanLeadEvents, roleColorKey, roleLabel,
} from '../../util/participantGroups';
import { ROLE_ADMIN, ROLE_ORGANIZER } from '../../util/auth';
import { endOfYear, formatDate, isoDate, rangeLabel, todayIso, yearOf } from './membershipDates';

const POPUP = {
  set: 'editplayersetrole',
  remove: 'editplayerremoverole',
  removeHistory: 'editplayerremoverolehistory',
  welcome: 'editplayerorgawelcome',
  error: 'editplayerroleerror',
};

const PREVIEW_URL = '/Role/Preview';
// Group choice of "Rolle setzen": a group id, KEEP_GROUP or NO_CHOICE — nothing is preselected, the API
// needs an explicit group or "Gruppe behalten".
const KEEP_GROUP = 'keep';
const NO_CHOICE = '';

const ROLE_ICON = { [ROLE.Organizer]: 'shield_person', [ROLE.Admin]: 'admin_panel_settings' };
// SegmentedControl knows only these tones; other role colors highlight neutrally.
const SEGMENT_TONES = ['positive', 'warning', 'accent', 'negative'];

// Configured names (/groups › Rollen); the active segment in the role color where possible.
const roleOptions = () => [ROLE.Organizer, ROLE.Admin].map(role => {
  const colorKey = roleColorKey(role);
  return {
    value: role,
    label: roleLabel(role),
    icon: ROLE_ICON[role],
    tone: SEGMENT_TONES.includes(colorKey) ? colorKey : 'neutral',
  };
});

// The API refuses changing one's own role; the page says so up front instead of failing on submit.
function ownPlayerId() {
  try {
    return Number(localStorage.getItem('playerId')) || null;
  } catch {
    return null;
  }
}

/**
 * Section "Rolle" of the player page: the fixed Orga/Admin axis (permissions, login account, event
 * leadership), separate from the groups. "Rolle setzen" also decides what happens to the membership
 * (group, open-ended) and shows /Role/Preview first; "Rolle entfernen" offers ending the membership.
 * Former role periods can be deleted from the history. After every change the parent reloads only the
 * player's group history (onChanged); the login account info is patched locally (onUserInfoChange).
 */
function PlayerRoleSection({ player, history, historyFailed, userInfo, onUserInfoChange, onChanged }) {
  const { active, byId, guest } = useParticipantGroups();
  // Re-render once the configured role names/colors are loaded (read through roleLabel/roleColorKey).
  useRoleSettings();
  const { openPopup, closePopup, isPopupOpen, popupData } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();

  // { kind: 'set' | 'remove', form } — kept after closing so the popup keeps its content while animating out.
  const [dialog, setDialog] = useState(null);
  const [dialogError, setDialogError] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingHistory, setPendingHistory] = useState(null);
  const [error, setError] = useState('');

  const today = todayIso();
  const playerName = `${player.firstName ?? ''} ${player.lastName ?? ''}`.trim();
  const selectable = useMemo(() => active.filter(g => !g.isGuest), [active]);
  const rolePeriods = history?.rolePeriods ?? [];
  const activePeriod = rolePeriods.find(r => r.isActive) ?? null;
  const activeRole = normalizeRole(activePeriod?.role) ?? normalizeRole(history?.currentRole);
  const pastPeriods = rolePeriods.filter(r => !r.isActive);
  const memberships = history?.memberships ?? [];
  const currentMembership = memberships.find(m => m.isCurrent) ?? null;
  const currentGroup = history?.currentGroup
    ? (byId.get(Number(history.currentGroup.id)) ?? normalizeParticipantGroup(history.currentGroup))
    : null;
  const currentMembershipName = currentMembership
    ? (byId.get(Number(currentMembership.participantGroupId))?.name ?? currentMembership.participantGroupName)
    : null;
  const guestName = guest?.name ?? 'Gast';
  const hasAccount = userInfo != null;
  const isSelf = ownPlayerId() === Number(player.id);

  // ── Dialogs ───────────────────────────────────────────────────────────────────────────────────
  const dialogOpen = !!dialog && isPopupOpen && popupData.name === POPUP[dialog.kind];

  const previewBody = useMemo(() => {
    if (!dialogOpen) return null;
    if (dialog.kind === 'remove') return { playerId: player.id, role: null, remove: true, endMembership: null };
    const f = dialog.form;
    // No preview before the group choice — the API refuses a request without one.
    if (f.groupChoice === NO_CHOICE) return null;
    const keep = f.groupChoice === KEEP_GROUP;
    return {
      playerId: player.id,
      role: f.role,
      participantGroupId: keep ? null : Number(f.groupChoice),
      keepGroup: keep,
      makeOpenEnded: keep ? true : f.makeOpenEnded,
      remove: false,
    };
  }, [dialogOpen, dialog, player.id]);

  const preview = usePreviewRequest(PREVIEW_URL, previewBody);
  const previewData = preview.data;

  const setForm = (patch) => {
    setDialogError('');
    setDialog(d => (d ? { ...d, form: { ...d.form, ...patch } } : d));
  };

  const openSet = () => {
    setDialog({
      kind: 'set',
      form: {
        role: activeRole === ROLE.Organizer ? ROLE.Admin : ROLE.Organizer,
        groupChoice: NO_CHOICE,
        makeOpenEnded: true,
        apply: true,
      },
    });
    setDialogError('');
    openPopup(POPUP.set, player.id);
  };

  const openRemove = () => {
    setDialog({ kind: 'remove', form: { apply: true, endMembership: null } });
    setDialogError('');
    openPopup(POPUP.remove, player.id);
  };

  // Default of "Mitgliedschaft beenden" = server default (open-ended + came with a role); the preview confirms it.
  const localEndDefault = !!currentMembership && !currentMembership.validTo
    && normalizeMembershipOrigin(currentMembership.origin) === 'RoleAssignment';
  const endMembershipChecked = dialog?.kind === 'remove'
    ? (dialog.form.endMembership ?? (previewData ? previewData.membershipAction === 'ended' : localEndDefault))
    : false;

  // "Rolle setzen" needs an explicit group choice (a group or "Gruppe behalten").
  const setHasGroupChoice = dialog?.kind === 'set' && dialog.form.groupChoice !== NO_CHOICE;

  const submitSet = () => {
    const f = dialog.form;
    if (f.groupChoice === NO_CHOICE) return;
    const keep = f.groupChoice === KEEP_GROUP;
    setSaving(true);
    setDialogError('');
    startSuccessBarLoading('Rolle wird gesetzt');
    api.post(`/Role/Set/${player.id}`, {
      role: f.role,
      applyForFutureEvents: !!f.apply,
      participantGroupId: keep ? null : Number(f.groupChoice),
      keepGroup: keep,
      makeOpenEnded: keep ? true : !!f.makeOpenEnded,
    })
      .then(res => {
        startSuccessBarSuccess(`${roleLabel(f.role)} gesetzt`);
        onUserInfoChange(prev => ({
          isRegistrationManager: prev?.isRegistrationManager ?? false,
          role: f.role === ROLE.Admin ? ROLE_ADMIN : ROLE_ORGANIZER,
        }));
        onChanged();
        // Replace the popup directly (closing first would race the exit animation).
        if (res.data?.isNewAccount) openPopup(POPUP.welcome, player.id);
        else closePopup();
      })
      .catch(err => {
        closeSuccessBar();
        setDialogError(apiErrorText(err, 'Die Rolle konnte nicht gesetzt werden.'));
      })
      .finally(() => setSaving(false));
  };

  const submitRemove = () => {
    const f = dialog.form;
    const end = currentMembership ? f.endMembership : null; // null → server default
    const query = `applyForFutureEvents=${!!f.apply}${end === null ? '' : `&endMembership=${end}`}`;
    setSaving(true);
    setDialogError('');
    startSuccessBarLoading('Rolle wird entfernt');
    api.delete(`/Role/Remove/${player.id}?${query}`)
      .then(() => {
        startSuccessBarSuccess('Rolle entfernt');
        onUserInfoChange(null);
        onChanged();
        closePopup();
      })
      .catch(err => {
        closeSuccessBar();
        setDialogError(apiErrorText(err, 'Die Rolle konnte nicht entfernt werden.'));
      })
      .finally(() => setSaving(false));
  };

  const submit = () => {
    if (saving || !dialog) return;
    if (dialog.kind === 'set') submitSet();
    else if (dialog.kind === 'remove') submitRemove();
  };

  const askRemoveHistory = (period) => {
    setPendingHistory(period);
    openPopup(POPUP.removeHistory, period.id);
  };

  const confirmRemoveHistory = () => {
    const period = pendingHistory;
    if (!period) return;
    startSuccessBarLoading('Historien-Eintrag wird gelöscht');
    api.delete(`/Role/RemoveHistory/${period.id}`)
      .then(() => {
        startSuccessBarSuccess('Historien-Eintrag gelöscht');
        onChanged();
      })
      .catch(err => {
        closeSuccessBar();
        setError(apiErrorText(err, 'Der Eintrag konnte nicht gelöscht werden.'));
        openPopup(POPUP.error, period.id);
      });
  };

  const handleRegistrationManagerChange = (e) => {
    const next = e.target.checked;
    onUserInfoChange(prev => (prev ? { ...prev, isRegistrationManager: next } : prev));
    api.put(`/User/SetRegistrationManager/${player.id}`, { isRegistrationManager: next })
      .then(res => onUserInfoChange(res.data))
      .catch(() => onUserInfoChange(prev => (prev ? { ...prev, isRegistrationManager: !next } : prev)));
  };

  // ── Popup bodies ──────────────────────────────────────────────────────────────────────────────
  const renderError = () => (dialogError ? <p className={'editPlayerPopupError'}>{dialogError}</p> : null);

  // /Role/Preview only warns "Ohne Gruppe bleibt der Spieler Gast …" when there is no membership to keep.
  const previewWarnings = previewData?.warnings ?? [];

  const renderImpact = (lines) => (
    <ImpactPreview
      lines={lines}
      warnings={previewWarnings}
      loading={preview.loading}
      failed={preview.failed}
    />
  );

  // Planned memberships (start after today) are replaced when the role creates/switches/extends one.
  const plannedMemberships = memberships.filter(m => !m.isCurrent && isoDate(m.validFrom) > today);

  const membershipActionLine = (f) => {
    const p = previewData;
    if (!p) return null;
    const keep = f.groupChoice === KEEP_GROUP;
    const target = keep ? null : byId.get(Number(f.groupChoice));
    const name = p.membershipGroupName ?? target?.name ?? '';
    const until = f.makeOpenEnded || target?.validityMode !== 'CalendarYear'
      ? 'unbefristet'
      : `bis ${formatDate(endOfYear(yearOf(today)))}`;
    switch (p.membershipAction) {
      case 'kept': return `Die Mitgliedschaft „${name}“ bleibt unverändert.`;
      case 'extended': return `Die Mitgliedschaft „${name}“ wird unbefristet.`;
      case 'switched': return `Gruppenwechsel: „${currentMembershipName ?? currentGroup?.name ?? ''}“ endet, ab heute gilt „${name}“ (${until}).`;
      case 'created': return `Neue Mitgliedschaft „${name}“ ab heute (${until}).`;
      default: return keep ? `Die Gruppe bleibt unverändert (${currentGroup?.name ?? guestName}).` : null;
    }
  };

  const renderSetBody = () => {
    const f = dialog.form;
    const keep = f.groupChoice === KEEP_GROUP;
    const chosen = f.groupChoice !== NO_CHOICE;
    const action = previewData?.membershipAction;
    const replacesPlanned = ['created', 'switched', 'extended'].includes(action) && plannedMemberships.length > 0;
    let accountLine = null;
    if (previewData?.createsAccount) {
      accountLine = previewData.sendsOrgaWelcome && player.email
        ? `Ein Login-Konto wird angelegt – das temporäre Passwort geht per Mail an ${player.email}.`
        : 'Ein Login-Konto wird angelegt.';
    } else if (hasAccount && f.role !== activeRole) {
      accountLine = `Das bestehende Login-Konto bekommt die Rolle ${roleLabel(f.role)}.`;
    }
    const lines = [
      membershipActionLine(f),
      replacesPlanned && `Geplante Mitgliedschaften werden ersetzt: ${plannedMemberships.map(m => `„${byId.get(Number(m.participantGroupId))?.name ?? m.participantGroupName}“ (${rangeLabel(m.validFrom, m.validTo, today)})`).join(', ')}.`,
      (action === 'created' || action === 'switched') && 'Es wird keine Willkommensmail der Gruppe versendet.',
      accountLine,
      futureParticipationsLine(previewData, { apply: f.apply, priceChanges: false }),
    ];
    return (
      <React.Fragment>
        <p className={'editPlayerPopupSubtitle'}>{playerName}{activeRole ? ` · aktuell ${roleLabel(activeRole)}` : ''}</p>
        {renderError()}
        <h3>Rolle</h3>
        <SegmentedControl options={roleOptions()} value={f.role} onChange={value => setForm({ role: value })} ariaLabel={'Rolle'} />
        <p className={'formFieldHint'}>{ROLE_PERMISSIONS_TEXT[f.role]}</p>
        {!roleCanLeadEvents(f.role) && (
          <p className={'formFieldHint'}>Diese Rolle leitet keine Spieltage.</p>
        )}
        <h3>Gruppe</h3>
        <CustomDropdown icon={'groups'} value={f.groupChoice} onChange={e => setForm({ groupChoice: e.target.value })}>
          <option value={NO_CHOICE} disabled>– Gruppe wählen –</option>
          {selectable.map(g => <option key={g.id} value={String(g.id)}>{g.name}</option>)}
          <option value={KEEP_GROUP}>Gruppe behalten ({currentGroup?.name ?? guestName})</option>
        </CustomDropdown>
        {!chosen && (
          <p className={'formFieldHint'}>Bitte eine Gruppe wählen oder „Gruppe behalten“ – danach siehst du die Auswirkungen.</p>
        )}
        {chosen && !keep && (
          <React.Fragment>
            <CustomTogglebox
              placeholder={'Mitgliedschaft unbefristet'}
              checked={!!f.makeOpenEnded}
              onChange={e => setForm({ makeOpenEnded: e.target.checked })}
            />
            <p className={'formFieldHint'}>Empfohlen – sonst fällt die Person nach Ablauf der Mitgliedschaft (z. B. am 31.12.) auf {guestName} zurück.</p>
          </React.Fragment>
        )}
        <CustomTogglebox
          placeholder={'Für zukünftige Spieltage übernehmen'}
          checked={!!f.apply}
          onChange={e => setForm({ apply: e.target.checked })}
        />
        {chosen && renderImpact(lines)}
      </React.Fragment>
    );
  };

  const renderRemoveBody = () => {
    const f = dialog.form;
    let membershipLine;
    if (!currentMembership) {
      membershipLine = `Keine Mitgliedschaft – ${playerName || 'der Spieler'} bleibt ${guestName}.`;
    } else if (endMembershipChecked) {
      membershipLine = isoDate(currentMembership.validFrom) >= today
        ? `Die heute begonnene Mitgliedschaft „${currentMembershipName}“ wird gelöscht.`
        : `Die Mitgliedschaft „${currentMembershipName}“ endet heute – ab morgen ist ${playerName || 'der Spieler'} ${guestName}.`;
    } else {
      membershipLine = `Die Mitgliedschaft „${currentMembershipName}“ bleibt bestehen (${currentMembership.validTo ? `bis ${formatDate(currentMembership.validTo)}` : 'unbefristet'}).`;
    }
    const lines = [
      membershipLine,
      hasAccount && 'Das Login-Konto wird gelöscht (inkl. Push-Benachrichtigungen und 2FA-Geräten) – eine Anmeldung ist danach nicht mehr möglich.',
      futureParticipationsLine(previewData, { apply: f.apply, priceChanges: false }),
    ];
    return (
      <React.Fragment>
        <p className={'editPlayerPopupSubtitle'}>{playerName}{activeRole ? ` · ${roleLabel(activeRole)}` : ''}</p>
        {renderError()}
        <p>Soll die Rolle <strong>{roleLabel(activeRole) || `${roleLabel(ROLE.Organizer)}/${roleLabel(ROLE.Admin)}`}</strong> wirklich entfernt werden?</p>
        {currentMembership && (
          <React.Fragment>
            <CustomTogglebox
              placeholder={`Mitgliedschaft „${currentMembershipName}“ beenden`}
              checked={endMembershipChecked}
              onChange={e => setForm({ endMembership: e.target.checked })}
            />
            <p className={'formFieldHint'}>Vorgabe: Eine über die Rolle entstandene, unbefristete Mitgliedschaft endet mit der Rolle – manuell angelegte bleiben bestehen.</p>
          </React.Fragment>
        )}
        <CustomTogglebox
          placeholder={'Für zukünftige Spieltage übernehmen'}
          checked={!!f.apply}
          onChange={e => setForm({ apply: e.target.checked })}
        />
        {renderImpact(lines)}
      </React.Fragment>
    );
  };

  // ── Section ───────────────────────────────────────────────────────────────────────────────────
  let body;
  if (historyFailed) {
    body = <p className={'playerRoleMessage'}>Rolle und Verlauf konnten nicht geladen werden.</p>;
  } else if (!history) {
    body = <p className={'playerRoleMessage'}>Lädt …</p>;
  } else {
    body = (
      <React.Fragment>
        <div className={'playerRoleCurrent'}>
          <ParticipantGroupDot role={activeRole} size={'md'} withTooltip={false} />
          <div className={'playerRoleCurrentText'}>
            <span className={'playerRoleCurrentName'}>{activeRole ? roleLabel(activeRole) : 'Keine Rolle'}</span>
            <span className={'playerRoleCurrentMeta'}>
              {activePeriod
                ? `seit ${formatDate(activePeriod.validFrom)}${hasAccount ? ' · Login-Konto aktiv' : ''}`
                : `Kein Login – ${roleLabel(ROLE.Organizer)} oder ${roleLabel(ROLE.Admin)} bekommen ein Konto für die Verwaltung.`}
            </span>
          </div>
        </div>

        {hasAccount && (
          <CustomTogglebox
            placeholder={'Anmelde-Manager (Anmelde-Pushes + Spieltag-Check)'}
            checked={!!userInfo?.isRegistrationManager}
            onChange={handleRegistrationManagerChange}
          />
        )}

        {isSelf ? (
          <p className={'playerRoleMessage'}>Deine eigene Rolle kannst du nicht ändern.</p>
        ) : (
          <div className={'playerRoleActions'}>
            <ActionButton icon={'shield_person'} label={activeRole ? 'Rolle ändern' : 'Rolle setzen'} type={3} onClick={openSet} />
            {activeRole && <ActionButton icon={'person_remove'} label={'Rolle entfernen'} type={1} onClick={openRemove} />}
          </div>
        )}

        {pastPeriods.length > 0 && (
          <div className={'playerRoleHistory'}>
            <span className={'playerRoleSubtitle'}>Verlauf</span>
            <ul className={'playerRoleHistoryList'}>
              {pastPeriods.map(r => (
                <li key={r.id} className={'playerRoleHistoryItem'}>
                  <ParticipantGroupDot role={r.role} withTooltip={false} />
                  <span className={'playerRoleHistoryName'}>{roleLabel(r.role)}</span>
                  <span className={'playerRoleHistoryDates'}>{rangeLabel(r.validFrom, r.validTo, today)}</span>
                  <ActionButton icon={'delete'} label={'Löschen'} type={1} onClick={() => askRemoveHistory(r)} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </React.Fragment>
    );
  }

  const confirmText = (label) => (saving ? 'Speichert …' : label);
  const popupProps = { withCancel: true, dontCloseOnConfirm: true, wide: true, onConfirm: submit };

  return (
    <section className={'playerRoleSection'} aria-label={'Rolle'}>
      <label className={'innerContainerLabel'}>Rolle</label>
      {body}

      <Popup name={POPUP.set} headerText={activeRole ? 'Rolle ändern' : 'Rolle setzen'}
        confirmText={confirmText('Rolle setzen')} confirmDisabled={!setHasGroupChoice} type={'info'} {...popupProps}>
        {dialog?.kind === 'set' && renderSetBody()}
      </Popup>
      <Popup name={POPUP.remove} headerText={'Rolle entfernen'}
        confirmText={confirmText('Rolle entfernen')} type={'warning'} {...popupProps}>
        {dialog?.kind === 'remove' && renderRemoveBody()}
      </Popup>
      <Popup name={POPUP.removeHistory} headerText={'Historien-Eintrag löschen'} confirmText={'Löschen'}
        type={'warning'} onConfirm={confirmRemoveHistory} withCancel>
        <p>
          Soll der Eintrag <strong>{roleLabel(pendingHistory?.role)}</strong>
          {pendingHistory ? ` (${rangeLabel(pendingHistory.validFrom, pendingHistory.validTo, today)})` : ''} wirklich dauerhaft gelöscht werden?
        </p>
        <p className={'formFieldHint'}>Der Eintrag kann nicht wiederhergestellt werden.</p>
      </Popup>
      <Popup name={POPUP.welcome} headerText={'Willkommensmail gesendet'} confirmText={'Ok'} type={'info'}>
        <p>Ein Login-Konto wurde für <strong>{playerName}</strong> angelegt.</p>
        <p className={'formFieldHint'}>Ein temporäres Passwort wurde automatisch generiert und per Mail an <strong>{player.email}</strong> gesendet.</p>
      </Popup>
      <Popup name={POPUP.error} headerText={'Fehler'} confirmText={'Ok'} type={'error'}>
        <p>{error}</p>
      </Popup>
    </section>
  );
}

export default PlayerRoleSection;
